/**
 * /mnemonic-table — страница: текст → клетки → картинки → лист, PDF/PNG/печать, показ.
 *
 * Состояние S живёт в браузере: черновик — __toolDraft.editor («Мои таблицы»), выбор
 * раскладки и вида — __toolSettings (умолчания следующей таблицы). Картинку к строке
 * подбирает match.js поверх общего Emoji.search (данные эмодзи и стеммер поиска сайта
 * грузятся тихо после открытия страницы, первый образец ждёт их не дольше загрузки).
 * Кап: единица — таблица (layout.unitKey: текст клеток + раскладка); списывает только
 * выдача, правка картинок и показ — нет, повтор той же таблицы в тот же день бесплатен.
 * В телеметрию — только счёт и выбор (раскладка, лист, доля подобранных картинок),
 * никакого текста. Разбор — docs/mnemonic-table.md.
 */
import { segment, createMatcher } from './match.js';
import { layoutSheet, unitKey } from './layout.js';
import { renderPages, drawPage, fitTexts } from './render.js';
import { createPictures } from './pictures.js';
import { createShow } from './show.js';

const SLUG = 'mnemonic-table';
const CFG = window.__MT || {};
const UI = CFG.i18n || {};
const MAX_CELLS = CFG.maxCells || 40;
const $ = (id) => document.getElementById(id);

function tr(key, params) {
    let v = UI[key];
    if (v == null) return '';
    v = String(v);
    if (params) Object.keys(params).sort((a, b) => b.length - a.length).forEach((k) => { v = v.split(':' + k).join(String(params[k])); });
    return v;
}
function plural(n, base) {
    const loc = CFG.locale || 'en';
    let form = '5';
    try {
        const r = new Intl.PluralRules(window.__intlLocale ? window.__intlLocale() : loc).select(n);
        form = r === 'one' ? '1' : (r === 'few' ? '2' : '5');
    } catch (_) { form = n === 1 ? '1' : '5'; }
    return n + ' ' + (UI[base + '_' + form] || UI[base + '_5'] || '');
}
function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (_) {} }
function announce(msg) { try { if (window.__announce) window.__announce(msg); } catch (_) {} }
function fu(action, data) { try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, action }, data || {})); } catch (_) {} }
function stage(name, meta) { try { if (window.__stage) window.__stage(name, meta || {}); } catch (_) {} }
function bucket(n) { return n <= 0 ? '0' : n <= 4 ? '1-4' : n <= 8 ? '5-8' : n <= 16 ? '9-16' : n <= 24 ? '17-24' : '25+'; }
function setting(k, d) { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, d) : d; } catch (_) { return d; } }
function remember(k, v) { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, k, v); } catch (_) {} }
function uid() { return 'm' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }
const normLine = (s) => String(s || '').toLowerCase().replace(/ё/g, 'е').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

/* ── состояние ────────────────────────────────────────────────────────── */
function freshState() {
    return {
        v: 1, sid: uid(), text: '', title: '', sample: '',
        perCell: 1, mode: setting('mode', 'auto'), cols: 0, perRow: 0, perPage: 6,
        sheet: 'child', style: setting('style', 'color'), paper: setting('paper', CFG.paper || 'a4'),
        orient: 'auto', numbers: true, arrows: true, showTitle: true, both: true,
        cells: [],
    };
}
let S = freshState();
const ui = { sel: -1, second: false, ready: false, matcher: null, busy: false };
let drafts = null, started = false, pictures = null, show = null, rebuildTimer = 0;

/* ── данные эмодзи и подбор ─────────────────────────────────────────────── */
let dataPromise = null;
function ensureData() {
    if (dataPromise) return dataPromise;
    dataPromise = new Promise((resolve, reject) => {
        let tries = 0;
        const go = () => {
            const E = window.Emoji;
            if (!E || !E.ready || !E.search || !E.search.rankWords) {
                if (tries++ < 100) return setTimeout(go, 100);
                return reject(new Error('emoji modules missing'));
            }
            E.ready().then(() => {
                const se = window.TimbricaSearch && window.TimbricaSearch._internal;
                ui.matcher = createMatcher({ search: E.search, emoji: E, lexicon: CFG.lexicon || [], locale: CFG.locale || 'en', normalize: se ? se.normalize : null });
                ui.ready = true;
                resolve(ui.matcher);
            }, reject);
        };
        go();
    });
    dataPromise.catch((err) => {
        try { if (window.__aeDiagnostic) window.__aeDiagnostic({ slug: SLUG, issue: 'emoji_data_failed' }); } catch (_) {}
        window.__handleError ? window.__handleError(err, 'mnemonic_data', { say: tr('err_data') }) : toast(tr('err_data'), 'error');
        dataPromise = null;
    });
    return dataPromise;
}

/** Текст → клетки; выбор человека у строк, которые остались, сохраняется. */
function rebuild() {
    const segs = segment(S.text, { perCell: S.perCell });
    if (segs.length > MAX_CELLS) toast(tr('err_too_long', { n: segs.length, max: MAX_CELLS }), 'warning');
    const list = segs.slice(0, MAX_CELLS);
    const old = new Map();
    S.cells.forEach((c) => { if (!c.auto) old.set(normLine(c.text), c); });
    const picks = ui.matcher ? ui.matcher.pickAll(list) : list.map(() => ({ hex: null, candidates: [] }));
    S.cells = list.map((sg, i) => {
        const keep = old.get(normLine(sg.text));
        if (keep) return { text: sg.text, stanza: sg.stanza, pics: keep.pics, auto: false };
        const h = picks[i].hex;
        return { text: sg.text, stanza: sg.stanza, pics: h ? [{ t: 'emoji', h }] : [], auto: true, first: h || '' };
    });
    if (ui.sel >= S.cells.length) ui.sel = S.cells.length ? S.cells.length - 1 : -1;
}
function scheduleRebuild() {
    clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(() => {
        const go = () => { rebuild(); paintAll(); touch(); };
        if (!S.text.trim()) { S.cells = []; ui.sel = -1; paintAll(); touch(); return; }
        if (ui.ready) go();
        else { setBusy(true); ensureData().then(go, go).finally(() => setBusy(false)); }
    }, 350);
}
function setBusy(on) { const st = $('mt-status'); if (st) { st.hidden = !on; st.textContent = on ? tr('loading') : ''; } }

/* ── черновики и «начато» ──────────────────────────────────────────────── */
function touch() { started = true; if (drafts) { try { drafts.touch(true); } catch (_) {} } }
function initDrafts() {
    if (!window.__toolDraft || !window.__toolDraft.editor || !$('mtDrafts')) return null;
    return window.__toolDraft.editor({
        slug: SLUG, root: 'mtDrafts', icon: '🖼️', openKey: 'mt-draft-open',
        payload: () => {
            if (!S.text.trim()) return null;
            const label = S.title || (S.cells[0] ? S.cells[0].text.split('\n')[0] : '') || tr('file_base');
            return { v: 1, state: JSON.parse(JSON.stringify(S)), label: label.slice(0, 80) + ' · ' + plural(S.cells.length, 'cells'), meta: { n: S.cells.length } };
        },
        apply: (d) => {
            const st = d && d.state;
            if (st && Array.isArray(st.cells)) S = Object.assign(freshState(), st);
            if (!S.sid) S.sid = uid();
            ui.sel = -1;
            syncControls();
            paintAll();
            return Promise.resolve([]);
        },
        fresh: () => { S = freshState(); ui.sel = -1; started = false; syncControls(); paintAll(); },
        started: () => started,
    });
}

/* ── предпросмотр ──────────────────────────────────────────────────────── */
function geoFor(sheet) {
    return layoutSheet({
        n: S.cells.length, stanzas: S.cells.map((c) => c.stanza || 0),
        mode: S.mode, cols: S.cols, perRow: S.perRow, perPage: S.perPage,
        sheet: sheet || S.sheet, paper: S.paper, orient: S.orient,
        title: S.showTitle && !!titleText(), arrows: S.arrows, rtl: !!CFG.rtl,
    });
}
function titleText() { return (S.title || '').trim(); }
function modelFor(sheet) { return { cells: S.cells, title: titleText(), sheet: sheet || S.sheet, style: S.style, numbers: S.numbers }; }
function cellLabel(i, c) { return tr('cell_aria', { n: i + 1, text: String(c.text || '').replace(/\n/g, ' ') }); }

let geo = null;
/** Пикселей в мм для предпросмотра: по ширине колонки, а на широком экране — не выше окна
 *  (книжный A4 во всю колонку 1600 px — это 2,2 тысячи пикселей вниз). */
function previewU(w) {
    const pg = geo.pages[0];
    let u = (w - 2) / pg.w;
    if (w >= 640) u = Math.min(u, Math.max(1.6, ((window.innerHeight || 900) - 150) / pg.h));
    return Math.max(1.2, Math.min(4.2, u));
}
function paintPreview() {
    const host = $('mt-sheets');
    const empty = $('mt-empty');
    const has = S.cells.length > 0;
    empty.hidden = has;
    host.hidden = !has;
    $('mt-stagebar').hidden = !has;
    if (!has) { host.textContent = ''; geo = null; return; }
    geo = geoFor();
    const w = host.clientWidth || host.parentElement.clientWidth || 600;
    const u = previewU(w);
    const r = renderPages(host, geo, modelFor(), { u, interactive: true, pictures, selected: ui.sel, rtl: !!CFG.rtl, credit: tr('foot_credit'), cellLabel });
    r.ready.then(() => { try { if (window.HtmlPdf) fitTexts(host); } catch (_) {} });
    const kept = S.cells.filter((c) => c.pics && c.pics.length).length;
    $('mt-summary').textContent = tr('summary', { cells: plural(S.cells.length, 'cells'), pages: plural(geo.pages.length, 'pages') });
    $('mt-found').textContent = tr('auto_kept', { n: kept, m: S.cells.length });
    schedulePaper();
}
function rescale() {
    const host = $('mt-sheets');
    if (!geo || host.hidden) return;
    const w = host.clientWidth || 600;
    const u = previewU(w);
    host.querySelectorAll('.mt-page').forEach((p) => p.style.setProperty('--u', u + 'px'));
    fitTexts(host);
}

/* ── клетка: варианты, поиск, своя картинка, рисунок ──────────────────── */
function hexToPic(h) { return { t: 'emoji', h }; }
function setPic(i, pic, via) {
    const c = S.cells[i];
    if (!c) return;
    if (ui.second && c.pics.length) c.pics = [c.pics[0], pic];
    else c.pics = pic ? [pic].concat(c.pics.slice(1, 2)) : [];
    if (!pic) c.pics = [];
    c.auto = false;
    ui.second = false;
    fu('pick', { via });
    paintPreview();
    paintEditor();
    touch();
}
function selectCell(i, focus) {
    if (i < 0 || i >= S.cells.length) { ui.sel = -1; paintEditor(); return; }
    ui.sel = i;
    ui.second = false;
    $('mt-sheets').querySelectorAll('.mt-cell').forEach((b) => b.classList.toggle('is-sel', +b.dataset.i === i));
    paintEditor();
    const ed = $('mt-ed');
    if (focus !== false && ed) {
        ed.scrollIntoView({ block: 'nearest' });
        const first = ed.querySelector('.mt-opt');
        if (first && focus === 'kbd') { try { first.focus({ preventScroll: true }); } catch (_) {} }
    }
}
function optButton(hex, on) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'mt-opt' + (on ? ' on' : '');
    b.dataset.hex = hex;
    const E = window.Emoji;
    const label = E && E.label ? E.label(hex) : hex;
    b.title = label;
    b.setAttribute('aria-label', label);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
    pictures.node(hexToPic(hex), 'color').then((n) => { if (n) b.appendChild(n); }).catch(() => {});
    return b;
}
function paintEditor() {
    const ed = $('mt-ed');
    if (!ed) return;
    const i = ui.sel, c = S.cells[i];
    ed.hidden = !c;
    if (!c) return;
    $('mt-ed-title').textContent = tr('ed_title', { n: i + 1 });
    $('mt-ed-line').textContent = c.text.replace(/\n/g, ' / ');
    const opts = $('mt-ed-opts');
    opts.textContent = '';
    const cur = (c.pics[0] && c.pics[0].t === 'emoji') ? c.pics[0].h : null;
    const cand = ui.matcher ? ui.matcher.pick(c.text, { n: 6 }).slice(0, 6).map((r) => r.hex) : [];
    if (cur && !cand.includes(cur)) cand.unshift(cur);
    cand.slice(0, 7).forEach((h) => opts.appendChild(optButton(h, h === cur)));
    $('mt-ed-nothing').hidden = cand.length > 0;
    $('mt-ed-second').textContent = ui.second ? tr('ed_second_on') : tr('ed_second');
    $('mt-ed-second').classList.toggle('on', ui.second);
    $('mt-ed-second').hidden = !c.pics.length;
    $('mt-ed-rm2').hidden = c.pics.length < 2;
    $('mt-ed-none').disabled = !c.pics.length;
    $('mt-ed-prev').disabled = i <= 0;
    $('mt-ed-next').disabled = i >= S.cells.length - 1;
    $('mt-ed-res').textContent = '';
    $('mt-ed-q').value = '';
}
function searchHexes(q) {
    if (!ui.matcher || !q.trim()) return [];
    const own = ui.matcher.pick(q, { n: 24, limit: 24 }).map((r) => r.hex);
    const E = window.Emoji;
    let sub = [];
    try { sub = E && E.search ? E.search.query(q).slice(0, 24) : []; } catch (_) {}
    const seen = new Set(), out = [];
    own.concat(sub).forEach((h) => { if (!seen.has(h)) { seen.add(h); out.push(h); } });
    return out.slice(0, 24);
}
function runSearch() {
    const q = $('mt-ed-q').value;
    const res = $('mt-ed-res');
    res.textContent = '';
    if (!q.trim()) return;
    const hexes = searchHexes(q);
    if (!hexes.length) { const p = document.createElement('p'); p.className = 'tc-hint'; p.textContent = tr('ed_search_none'); res.appendChild(p); return; }
    hexes.forEach((h) => res.appendChild(optButton(h, false)));
    fu('search', { n: bucket(hexes.length) });
}
function openPicker(btn) {
    const i = ui.sel;
    const go = () => {
        if (!window.EmojiPicker || !window.EmojiPicker.openFor) return;
        window.EmojiPicker.openFor(btn, {
            source: SLUG, style: 'fluent-flat',
            search: (q) => searchHexes(q),
            onPick: (glyph, em) => { if (em && em.h) setPic(i, hexToPic(em.h), 'picker'); },
        });
    };
    if (window.EmojiPicker && !window.EmojiPicker._stub) go();
    else if (window.EmojiPicker && window.EmojiPicker.openFor) go();
    fu('picker_open');
}
/** Своя картинка: уменьшить до 768 px по большей стороне, JPEG (фото) или PNG (прозрачный рисунок). */
async function fileToPic(file) {
    let bmp;
    try { bmp = await createImageBitmap(file); } catch (_) {
        bmp = await new Promise((resolve, reject) => {
            const url = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => { resolve(img); setTimeout(() => URL.revokeObjectURL(url), 0); };
            img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
            img.src = url;
        });
    }
    const w0 = bmp.width, h0 = bmp.height, k = Math.min(1, 768 / Math.max(w0, h0));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w0 * k)); c.height = Math.max(1, Math.round(h0 * k));
    const x = c.getContext('2d');
    x.drawImage(bmp, 0, 0, c.width, c.height);
    const png = /png|gif|webp|svg/.test(file.type || '');
    return { t: 'img', src: c.toDataURL(png ? 'image/png' : 'image/jpeg', 0.86) };
}

let pad = null;
function openDraw() {
    const dlg = $('mt-draw');
    if (!dlg || !window.SignaturePad) return;
    $('mt-draw-title').textContent = tr('draw_title', { n: ui.sel + 1 });
    if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', '');
    const canvas = $('mt-draw-canvas');
    if (!pad) {
        pad = window.SignaturePad.create({ canvas, host: canvas.parentElement, color: '#1f2937', penSize: 5, guideLine: false, smoothing: 45 });
    } else { pad.clear(); pad.resize && pad.resize(); }
    pad.setMode && pad.setMode('draw');
    fu('draw_open');
}
async function finishDraw() {
    const dlg = $('mt-draw');
    if (!pad || pad.isEmpty()) { toast(tr('draw_empty'), 'warning'); return; }
    try {
        const blob = await pad.exportBlob();
        const src = await new Promise((resolve, reject) => { const fr = new FileReader(); fr.onload = () => resolve(fr.result); fr.onerror = reject; fr.readAsDataURL(blob); });
        setPic(ui.sel, { t: 'draw', src }, 'draw');
    } catch (err) { window.__handleError && window.__handleError(err, 'mnemonic_draw', { say: tr('err_img') }); }
    try { dlg.close(); } catch (_) { dlg.removeAttribute('open'); }
}

/* ── выдача ────────────────────────────────────────────────────────────── */
function begin() {
    return window.__toolLimit ? window.__toolLimit.begin(SLUG, [unitKey(S.cells, S)]) : { commit() {}, abort() {} };
}
function busy(btn, on) { if (btn) { btn.disabled = !!on; btn.classList.toggle('is-busy', !!on); } }
function fileBase() {
    const t = titleText() || (S.cells[0] ? S.cells[0].text.split('\n')[0] : '') || tr('file_base');
    try { if (window.__outputName && window.__outputName.sanitize) return window.__outputName.sanitize(tr('file_base') + ' — ' + t).slice(0, 90) || tr('file_base'); } catch (_) {}
    return (tr('file_base') + ' - ' + t).replace(/[\\/:*?"<>|;]+/g, ' ').slice(0, 90);
}
function sheetsForFile() { return S.both ? ['child', 'adult'] : [S.sheet]; }
function completeStats(action) {
    const autoKept = S.cells.filter((c) => c.auto && c.pics.length).length;
    try {
        if (window.__aeToolComplete) window.__aeToolComplete({
            action, layout: geo ? geo.mode : S.mode, sheet: S.both ? 'both' : S.sheet, style: S.style,
            cells: bucket(S.cells.length), auto_kept: S.cells.length ? String(Math.round(10 * autoKept / S.cells.length) * 10) : '0',
            custom: bucket(S.cells.filter((c) => c.pics.some((p) => p.t !== 'emoji')).length),
        });
    } catch (_) {}
}
/** Листы для файла: вне экрана, в настоящих мм (HtmlPdf меряет этот DOM).
 *  Собираются заранее, пока человек смотрит на таблицу: печать должна открыть окно
 *  в том же нажатии (Safari и Firefox режут window.open после await), а PDF — не ждать. */
let paperSig = '', paperJob = null, paperTimer = 0;
function sigNow() {
    return JSON.stringify([S.cells.map((c) => [c.text, c.pics]), titleText(), S.mode, S.cols, S.perRow, S.perPage, S.style, S.numbers, S.arrows, S.showTitle, S.both, S.both ? '' : S.sheet, S.paper, S.orient]);
}
function ensurePaper() {
    const sig = sigNow();
    if (paperSig === sig && !paperJob) return Promise.resolve();
    if (paperJob && paperJob.sig === sig) return paperJob.p;
    const job = { sig, p: null };
    job.p = buildPaper().then(() => { if (paperJob === job) { paperSig = sig; paperJob = null; } }, (err) => { if (paperJob === job) paperJob = null; paperSig = ''; throw err; });
    paperJob = job;
    return job.p;
}
function schedulePaper() {
    clearTimeout(paperTimer);
    if (!S.cells.length) return;
    paperTimer = setTimeout(() => { ensurePaper().catch(() => {}); }, 900);
}
async function buildPaper() {
    const host = $('mt-paper');
    host.textContent = '';
    const font = window.HtmlPdf && window.HtmlPdf.FAMILY ? window.HtmlPdf.FAMILY.sans : null;
    const all = [];
    for (const sh of sheetsForFile()) {
        const g = geoFor(sh);
        const box = document.createElement('div');
        host.appendChild(box);
        const r = renderPages(box, g, modelFor(sh), { u: 96 / 25.4, interactive: false, pictures, rtl: !!CFG.rtl, credit: tr('foot_credit'), font });
        await r.ready;
        all.push({ geo: g, pages: r.pages });
    }
    return all;
}
async function deliver(kind, btn) {
    if (!S.cells.length) { toast(tr('err_empty'), 'warning'); return; }
    const run = begin();
    if (!run) return;
    busy(btn, true);
    let ok = false;
    try {
        if (kind === 'pdf') {
            if (!window.HtmlPdf) throw Object.assign(new Error('htmlpdf_missing'), { say: tr('err_pdf') });
            try { window.HtmlPdf.installFonts(); } catch (_) {}
            await ensurePaper();
            const bytes = await window.HtmlPdf.fromElement($('mt-paper'), {
                pages: 'css', pageSelector: '.mt-page', title: titleText() || tr('file_base'), lang: CFG.locale || 'en',
                creator: 'Timbrica', producer: 'Timbrica HtmlPdf', subject: tr('file_base'),
            });
            ok = await window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), fileBase() + '.pdf');
            if (ok) toast(tr('done_pdf'), 'success');
        } else if (kind === 'png') {
            const g = geoFor(S.sheet);
            const k = 150 / 25.4;
            const canvases = [];
            for (const pg of g.pages) canvases.push(await drawPage(pg, modelFor(S.sheet), { pictures, rtl: !!CFG.rtl, credit: tr('foot_credit') }, k));
            let out = canvases[0];
            if (canvases.length > 1) {
                out = document.createElement('canvas');
                out.width = canvases[0].width;
                out.height = canvases.reduce((a, c) => a + c.height, 0) + (canvases.length - 1) * 24;
                const x = out.getContext('2d');
                x.fillStyle = '#e5e7eb'; x.fillRect(0, 0, out.width, out.height);
                let y = 0;
                canvases.forEach((c) => { x.drawImage(c, 0, y); y += c.height + 24; });
            }
            const blob = await window.__canvasBlob(out, 'image/png');   // общий примитив: отказ кодировщика — ошибка, а не null
            ok = await window.__downloadFile(blob, fileBase() + ' — ' + tr(S.sheet === 'adult' ? 'sheet_adult_short' : 'sheet_child_short') + '.png');
            if (ok) toast(tr('done_png'), 'success');
        } else if (kind === 'print') {
            // Листы готовы — окно открывается в том же нажатии; нет — после сборки (может быть заблокировано).
            if (paperSig !== sigNow() || paperJob) await ensurePaper();
            const w = window.__printWindow ? window.__printWindow(printHtml(), { delay: 400, onBlocked: () => toast(tr('err_print'), 'error') }) : null;
            ok = !!w;
        }
        if (ok) {
            run.commit();
            if (drafts) { try { drafts.exported(); } catch (_) {} }
            stage('export', { fmt: kind });
            completeStats(kind);
        } else run.abort();
    } catch (err) {
        run.abort();
        paperSig = '';
        const say = (err && err.say) || tr(kind === 'png' ? 'err_png' : 'err_pdf');
        window.__handleError ? window.__handleError(err, 'mnemonic_' + kind, { say: say }) : toast(say, 'error');
    } finally { busy(btn, false); }
}
function printHtml() {
    const paper = $('mt-paper');
    const pages = Array.from(paper.querySelectorAll('.mt-page'));
    const css = (document.getElementById('mt-sheet-css') || {}).textContent || '';
    const first = pages[0];
    const w = first ? first.style.width : '', h = first ? first.style.height : '';
    const port = first && parseFloat(getComputedStyle(first).height) > parseFloat(getComputedStyle(first).width);
    const body = pages.map((p) => p.outerHTML).join('');
    return '<!doctype html><html lang="' + (CFG.locale || 'en') + '"' + (CFG.rtl ? ' dir="rtl"' : '') + '><head><meta charset="utf-8"><title>' +
        String(titleText() || tr('file_base')).replace(/[<&]/g, '') + '</title><style>' + css +
        '@page{size:' + (S.paper === 'letter' ? 'letter' : 'A4') + ' ' + (port ? 'portrait' : 'landscape') + ';margin:0}' +
        'html,body{margin:0;padding:0;background:#fff}.mt-page{page-break-after:always;break-after:page;box-shadow:none!important;margin:0!important;border-radius:0!important}' +
        '.mt-page:last-child{page-break-after:auto;break-after:auto}' +
        '</style></head><body>' + body + '</body></html>' + (w && h ? '' : '');
}

/* ── управление ────────────────────────────────────────────────────────── */
function segSet(id, val) {
    const g = $(id);
    if (!g) return;
    g.querySelectorAll('[data-v]').forEach((b) => { const on = b.dataset.v === String(val); b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
}
function syncControls() {
    $('mt-text').value = S.text;
    $('mt-title').value = S.title;
    segSet('mt-percell', S.perCell);
    segSet('mt-mode', S.mode);
    segSet('mt-sheet', S.sheet);
    segSet('mt-style', S.style);
    segSet('mt-paper-seg', S.paper);
    segSet('mt-orient', S.orient);
    segSet('mt-cols', S.cols || 0);
    segSet('mt-perrow', S.perRow || 0);
    segSet('mt-perpage', S.perPage || 6);
    $('mt-numbers').checked = S.numbers;
    $('mt-arrows').checked = S.arrows;
    $('mt-showtitle').checked = S.showTitle;
    $('mt-both').checked = S.both;
    $('mt-cols-f').hidden = S.mode !== 'table';
    $('mt-perrow-f').hidden = S.mode !== 'track';
    $('mt-perpage-f').hidden = S.mode !== 'cards';
    $('mt-arrows-f').hidden = !(S.mode === 'track' || (S.mode === 'auto' && S.cells.length <= 3));
    document.querySelectorAll('#mt-samples [data-sample]').forEach((b) => b.classList.toggle('on', b.dataset.sample === S.sample && !!S.sample));
}
function paintAll() { syncControls(); paintPreview(); paintEditor(); }

function wireSeg(id, key, after) {
    const g = $(id);
    if (!g) return;
    g.addEventListener('click', (e) => {
        const b = e.target.closest('[data-v]');
        if (!b) return;
        const raw = b.dataset.v;
        S[key] = /^\d+$/.test(raw) ? +raw : raw;
        if (after) after(S[key]);
        fu(key, { v: String(S[key]) });
        paintAll();
        touch();
    });
}
function useSample(id) {
    const smp = (CFG.samples || []).find((s) => s.id === id);
    if (!smp) return;
    if (S.text.trim() && S.sample !== id && !S.sample) {
        if (drafts) { try { drafts.leave(); } catch (_) {} }
        S = freshState();
    }
    S.text = smp.text;
    S.title = smp.title;
    S.sample = id;
    S.cells = [];
    ui.sel = -1;
    fu('sample', { id });
    syncControls();
    setBusy(true);
    ensureData().then(() => { rebuild(); paintAll(); touch(); stage('table', { n: bucket(S.cells.length), src: 'sample' }); announce(tr('sample_title', { title: smp.title })); }, () => {}).finally(() => setBusy(false));
}

function wire() {
    const ta = $('mt-text');
    ta.addEventListener('input', () => {
        S.text = ta.value;
        if (S.sample) {
            const smp = (CFG.samples || []).find((x) => x.id === S.sample);
            if (smp && S.title === smp.title && S.text.indexOf(smp.text.split('\n')[0]) < 0) { S.title = ''; $('mt-title').value = ''; }
            S.sample = '';
        }
        scheduleRebuild();
        if (!ui.typed) { ui.typed = true; stage('text'); }
    });
    ta.addEventListener('focus', () => { ensureData().catch(() => {}); }, { once: true });
    $('mt-title').addEventListener('input', (e) => { S.title = e.target.value; clearTimeout(ui.tt); ui.tt = setTimeout(() => { paintPreview(); touch(); }, 250); });
    ['mt-samples', 'mt-samples2'].forEach((id) => { const g = $(id); if (g) g.addEventListener('click', (e) => { const b = e.target.closest('[data-sample]'); if (b) useSample(b.dataset.sample); }); });
    wireSeg('mt-percell', 'perCell', () => { rebuildKeep(); });
    wireSeg('mt-mode', 'mode', (v) => remember('mode', v));
    wireSeg('mt-sheet', 'sheet');
    wireSeg('mt-style', 'style', (v) => remember('style', v));
    wireSeg('mt-paper-seg', 'paper', (v) => remember('paper', v));
    wireSeg('mt-orient', 'orient');
    wireSeg('mt-cols', 'cols');
    wireSeg('mt-perrow', 'perRow');
    wireSeg('mt-perpage', 'perPage');
    [['mt-numbers', 'numbers'], ['mt-arrows', 'arrows'], ['mt-showtitle', 'showTitle'], ['mt-both', 'both']].forEach(([id, k]) => {
        $(id).addEventListener('change', (e) => { S[k] = !!e.target.checked; fu(k, { on: S[k] ? '1' : '0' }); paintPreview(); touch(); });
    });
    // клетка листа
    $('mt-sheets').addEventListener('click', (e) => {
        const b = e.target.closest('.mt-cell');
        if (!b) return;
        selectCell(+b.dataset.i, e.detail === 0 ? 'kbd' : true);
        fu('cell_open');
    });
    // редактор клетки
    $('mt-ed').addEventListener('click', (e) => {
        const o = e.target.closest('.mt-opt');
        if (o) { setPic(ui.sel, hexToPic(o.dataset.hex), o.closest('#mt-ed-res') ? 'search' : 'alt'); return; }
    });
    $('mt-ed-none').addEventListener('click', () => setPic(ui.sel, null, 'none'));
    $('mt-ed-second').addEventListener('click', () => { ui.second = !ui.second; paintEditor(); });
    $('mt-ed-rm2').addEventListener('click', () => { const c = S.cells[ui.sel]; if (c) { c.pics = c.pics.slice(0, 1); c.auto = false; paintPreview(); paintEditor(); touch(); } });
    $('mt-ed-prev').addEventListener('click', () => selectCell(ui.sel - 1, false));
    $('mt-ed-next').addEventListener('click', () => selectCell(ui.sel + 1, false));
    $('mt-ed-done').addEventListener('click', () => { const i = ui.sel; selectCell(-1); const b = $('mt-sheets').querySelector('.mt-cell[data-i="' + i + '"]'); if (b) { try { b.focus({ preventScroll: true }); } catch (_) {} } });
    $('mt-ed-q').addEventListener('input', () => { clearTimeout(ui.qt); ui.qt = setTimeout(runSearch, 200); });
    $('mt-ed-q').addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } });
    $('mt-ed-more').addEventListener('click', (e) => openPicker(e.currentTarget));
    $('mt-ed-draw').addEventListener('click', openDraw);
    const zone = $('mt-up');
    if (zone) zone.addEventListener('tool-uploader:file', async (e) => {
        const f = e.detail && e.detail.file;
        const i = ui.sel;
        if (!f || i < 0) return;
        try { setPic(i, await fileToPic(f), 'upload'); }
        catch (err) { window.__handleError ? window.__handleError(err, 'mnemonic_upload', { say: tr('err_img') }) : toast(tr('err_img'), 'error'); }
        finally { try { if (zone.__uploaderReset) zone.__uploaderReset(); } catch (_) {} }
    });
    // рисунок
    $('mt-draw-undo').addEventListener('click', () => { if (pad) pad.undo(); });
    $('mt-draw-clear').addEventListener('click', () => { if (pad) pad.clear(); });
    $('mt-draw-done').addEventListener('click', finishDraw);
    $('mt-draw-cancel').addEventListener('click', () => { const d = $('mt-draw'); try { d.close(); } catch (_) { d.removeAttribute('open'); } });
    $('mt-draw').addEventListener('click', (e) => { const sw = e.target.closest('[data-color]'); if (sw && pad) { pad.setOption && pad.setOption('color', sw.dataset.color); $('mt-draw').querySelectorAll('[data-color]').forEach((b) => b.classList.toggle('on', b === sw)); } });
    // выдача и показ
    $('mt-pdf').addEventListener('click', (e) => deliver('pdf', e.currentTarget));
    $('mt-png').addEventListener('click', (e) => deliver('png', e.currentTarget));
    $('mt-print').addEventListener('click', (e) => deliver('print', e.currentTarget));
    $('mt-show-btn').addEventListener('click', () => {
        if (!S.cells.length) { toast(tr('err_empty'), 'warning'); return; }
        show.start({ cells: S.cells, title: titleText() });
    });
    // ширина колонки меняется — масштаб листа следом
    if (typeof ResizeObserver === 'function') {
        let rt = 0;
        new ResizeObserver(() => { cancelAnimationFrame(rt); rt = requestAnimationFrame(rescale); }).observe($('mt-sheets'));
    }
}
/** «Строк в клетке» меняет клетки целиком: выбор человека переносится по совпавшему тексту. */
function rebuildKeep() { if (ui.ready) rebuild(); else scheduleRebuild(); }

/* ── старт ─────────────────────────────────────────────────────────────── */
function boot() {
    if (!$('mt')) return;
    pictures = createPictures({ emoji: { get: (h) => (window.Emoji && window.Emoji.get ? window.Emoji.get(h) : null) } });
    show = createShow({ root: $('mt-show'), tr, pictures });
    wire();
    drafts = initDrafts();
    if (drafts) drafts.boot();
    paintAll();
    $('mt').classList.add('is-ready');
    stage('ready');
    // Данные эмодзи (~120 КБ сжатыми, кэш service worker'а) — тихо, когда страница отрисовалась.
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1200));
    idle(() => { ensureData().then(() => { if (S.text.trim() && S.cells.length === 0) { rebuild(); paintAll(); } }).catch(() => {}); }, { timeout: 2500 });
    try { if (window.EmojiRender && window.EmojiRender.ready) window.EmojiRender.ready(); } catch (_) {}
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
